import HelpPanel from '@risk-smart/themed-cloudscape-components/help-panel';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Spinner from '@risk-smart/themed-cloudscape-components/spinner';
import Button from '@risksmart-app/components/src/button';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { Inbox01, Settings03 } from '@untitled-ui/icons-react';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Tooltip } from 'react-tooltip';
import {
  AUTO_LOAD_ITEM_LIMIT,
  useMessagesFeedStore,
} from 'src/components/messages/useMessagesFeedStore';

import ConfirmModal from '@/components/confirm-modal/ConfirmModal';
import NotificationSettingsModal from '@/components/notification-settings-modal/NotificationSettingsModal';

import EmptyNotifications from './EmptyNotifications';
import { NotificationCell } from './NotificationCell';
import styles from './style.module.scss';
import type { NotificationItem } from './useNotificationItems';
import { useNotificationItems } from './useNotificationItems';

const SETTINGS_TOOLTIP_ID = 'notifications-settings';
const CLEAR_TOOLTIP_ID = 'notifications-clear-all';

type Props = {
  loading: boolean;
  items: NotificationItem[];
  hasMore: boolean;
  onClearAll: () => void | Promise<void>;
  onLoadMore: () => void | Promise<void>;
  onRead: (item: NotificationItem) => void;
  onRemove: (item: NotificationItem) => void;
  onShowSettings: () => void;
};

export const NotificationsList: FC<Props> = ({
  loading,
  items,
  hasMore,
  onClearAll,
  onLoadMore,
  onRead,
  onRemove,
  onShowSettings,
}) => {
  const { t } = useTranslation(['common'], { keyPrefix: 'notifications' });
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Each loaded page widens the entity-lookup query, which reports loading again.
  // Gating on `loading` alone swapped the whole list for a spinner every page.
  if (loading && items.length === 0) {
    return (
      <div className={'flex items-center justify-center h-full'}>
        <Spinner size={'large'} />
      </div>
    );
  }

  const confirmClear = async () => {
    setClearing(true);
    try {
      await onClearAll();
    } finally {
      setClearing(false);
      setConfirmingClear(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      await onLoadMore();
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className={styles.notificationList}>
      <HelpPanel
        header={
          <div className={'flex items-center justify-between -mt-2'}>
            <h2>{t('header')}</h2>
            <div className={'flex flex-row'}>
              <button
                onClick={onShowSettings}
                aria-label={t('settings_button')}
                data-tooltip-id={SETTINGS_TOOLTIP_ID}
                className={
                  'bg-transparent border-0 hover:bg-grey150 text-grey500 flex items-center justify-center text-center p-1 rounded-md cursor-pointer w-[40px] h-[40px]'
                }
              >
                <Settings03 />
              </button>
              <button
                onClick={() => setConfirmingClear(true)}
                aria-label={t('clear_button')}
                data-tooltip-id={CLEAR_TOOLTIP_ID}
                className={
                  'bg-transparent border-0 hover:bg-grey150 text-grey500 flex items-center justify-center text-center p-1 rounded-md cursor-pointer w-[40px] h-[40px]'
                }
              >
                <Inbox01 />
              </button>
              <Tooltip
                id={SETTINGS_TOOLTIP_ID}
                place={'bottom'}
                variant={'dark'}
                delayShow={500}
                className={styles.tooltip}
              >
                {t('settings_button')}
              </Tooltip>
              <Tooltip
                id={CLEAR_TOOLTIP_ID}
                place={'bottom'}
                variant={'dark'}
                delayShow={500}
                className={styles.tooltip}
              >
                {t('clear_button')}
              </Tooltip>
            </div>
          </div>
        }
      >
        {items.length === 0 && <EmptyNotifications />}
        <SpaceBetween size={'s'}>
          {items.map((item) => (
            <NotificationCell
              key={item.feedItem.id}
              item={item}
              onArchive={() => onRemove(item)}
              onRead={() => onRead(item)}
            />
          ))}
        </SpaceBetween>
        {hasMore && items.length > 0 && (
          <div className={'flex justify-center mt-3'}>
            <Button variant={'normal'} loading={loadingMore} onClick={loadMore}>
              {t('load_more')}
            </Button>
          </div>
        )}
      </HelpPanel>

      <ConfirmModal
        isVisible={confirmingClear}
        loading={clearing}
        header={t('clear_confirm_header')}
        onConfirm={confirmClear}
        onDismiss={() => setConfirmingClear(false)}
      >
        {t('clear_confirm_body')}
      </ConfirmModal>
    </div>
  );
};

const WrappedNotificationList: FC = () => {
  const { t } = useTranslation(['common'], { keyPrefix: 'notifications' });
  const { items, loading } = useNotificationItems();
  const { addNotification } = useNotifications();
  const markAsRead = useMessagesFeedStore((state) => state.markAsRead);
  const markAsArchived = useMessagesFeedStore((state) => state.markAsArchived);
  const markAllAsArchived = useMessagesFeedStore(
    (state) => state.markAllAsArchived
  );
  const fetchFeed = useMessagesFeedStore((state) => state.fetch);
  const fetchNextPage = useMessagesFeedStore((state) => state.fetchNextPage);
  const fetchUpTo = useMessagesFeedStore((state) => state.fetchUpTo);
  const pageInfo = useMessagesFeedStore((state) => state.pageInfo);
  const [showSettings, setShowSettings] = useState(false);
  const navigate = useNavigate();

  const notifyIfPageFailed = useCallback(
    (loaded: boolean) => {
      if (!loaded) {
        addNotification({ type: 'error', content: <>{t('load_failed')}</> });
      }
    },
    [addNotification, t]
  );

  // The drawer mounts this component on open, so this fills the panel per open
  // rather than per session. Keyed on the cursor too because the first page can
  // land after mount; re-entry is safe while a walk is already running.
  useEffect(() => {
    void fetchUpTo(AUTO_LOAD_ITEM_LIMIT).then(notifyIfPageFailed);
  }, [fetchUpTo, pageInfo.after, notifyIfPageFailed]);

  return (
    <>
      {showSettings && (
        <NotificationSettingsModal onClose={() => setShowSettings(false)} />
      )}
      <NotificationsList
        items={items}
        hasMore={pageInfo.after !== null}
        onRemove={(item) => markAsArchived(item.feedItem)}
        onRead={(item) => {
          if (item.url) {
            navigate(item.url);
          }
          markAsRead(item.feedItem);
        }}
        loading={loading}
        onClearAll={async () => {
          if (await markAllAsArchived()) {
            return;
          }

          // Knock emptied its store before the request failed, so the panel is
          // showing a clear that did not happen. Re-read the feed so it reflects
          // what is actually still there.
          addNotification({ type: 'error', content: <>{t('clear_failed')}</> });
          fetchFeed();
        }}
        onLoadMore={async () => notifyIfPageFailed(await fetchNextPage())}
        onShowSettings={() => setShowSettings(true)}
      />
    </>
  );
};

export default WrappedNotificationList;
